import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Pagination, message } from 'antd';
import { api } from '../../utils/api';

interface AppListItem {
  id: number;
  name: string;
  description: string;
  logoUrl: string;
  latestVersion: string | null;
}

const AdminAppsPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<AppListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await api.get('/api/admin/apps', { params: { page, pageSize } });
        if (!alive) return;
        setItems(res.data.items);
        setTotal(res.data.total);
      } catch {
        setItems([]);
        setTotal(0);
      } finally {
        if (alive) setLoading(false);
      }
    })().catch(() => {
      setItems([]);
      setTotal(0);
    });
    return () => { alive = false; };
  }, [page]);

  const editApp = (id: number) => {
    navigate(`/admin/apps/${id}/edit`);
  };

  const updateVersion = (id: number) => {
    navigate(`/admin/apps/${id}/versions`);
  };

  const confirmDeleteApp = (app: AppListItem) => {
    Modal.confirm({
      title: 'アプリを削除しますか？',
      content: `「${app.name}」を削除すると復元できません。よろしいですか？`,
      okText: '削除',
      okButtonProps: { danger: true },
      cancelText: 'キャンセル',
      onOk: async () => {
        try {
          await api.delete(`/api/admin/apps/${app.id}`);
          message.success('アプリを削除しました。');
          // refresh
          const res = await api.get('/api/admin/apps', { params: { page, pageSize } });
          setItems(res.data.items);
          setTotal(res.data.total);
        } catch {
          message.error('削除に失敗しました。');
        }
      },
    });
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="m-0 text-xl font-semibold">アプリ管理</h2>
        <button onClick={() => navigate('/admin/apps/new')} className="rounded-md bg-[#00a0e9] px-3 py-2 text-white">アプリ登録</button>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="w-full table-fixed border-collapse">
          <thead className="bg-gray-100">
            <tr>
              <th className="w-32 border-b border-gray-200 px-3 py-2 text-left">ロゴ</th>
              <th className="w-56 border-b border-gray-200 px-3 py-2 text-left">名前</th>
              <th className="border-b border-gray-200 px-3 py-2 text-left">説明</th>
              <th className="w-40 border-b border-gray-200 px-3 py-2 text-left">最新バージョン</th>
              <th className="w-80 border-b border-gray-200 px-3 py-2 text-center">アクション</th>
            </tr>
          </thead>
          <tbody>
            {items.map((app) => (
              <tr key={app.id} className="align-middle">
                <td className="border-b border-gray-100 px-3 py-2">
                  <img src={app.logoUrl} alt={`${app.name} のロゴ`} className="h-10 w-10 object-contain" />
                </td>
                <td className="text-left border-b border-gray-100 px-3 py-2 font-semibold">{app.name}</td>
                <td className="text-left border-b border-gray-100 px-3 py-2 text-gray-600">{app.description}</td>
                <td className="text-left border-b border-gray-100 px-3 py-2">{app.latestVersion ?? '-'}</td>
                <td className="text-left border-b border-gray-100 px-3 py-2">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => editApp(app.id)}
                      className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-gray-800"
                    >編集</button>
                    <button
                      onClick={() => updateVersion(app.id)}
                      className="rounded-md border border-[#006bb3] bg-[#0077C8] px-3 py-1.5 text-white"
                    >バージョン更新</button>
                    <button
                      onClick={() => confirmDeleteApp(app)}
                      className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-red-600"
                    >削除</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-end gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 mr-auto">全 {total}件</span>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            onChange={(p) => setPage(p)}
            showSizeChanger={false}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminAppsPage;
